import { ensureAbsoluteAppAssetUrl } from "~/lib/resolve-app-asset-url";

const APP_IMAGE_HOSTS =
  /(^|\.)(easygpt\.builders|siteglue\.ai|inboundgpt\.ai|practiceclaw\.com)$/i;

/**
 * Preset avatars under /images/… are served from every brand host.
 * Absolute app.easygpt.builders URLs break next/image on siteglue.ai (and vice versa).
 */
export function preferSameOriginAvatarSrc(src: string): string {
  const trimmed = src.trim();
  if (!trimmed) return trimmed;
  if (trimmed.startsWith("/") && !trimmed.startsWith("//")) return trimmed;
  try {
    const url = new URL(trimmed);
    if (url.pathname.startsWith("/images/") && APP_IMAGE_HOSTS.test(url.hostname)) {
      return `${url.pathname}${url.search}`;
    }
  } catch {
    /* ignore */
  }
  return trimmed;
}

/**
 * Normalize AI agent avatar for <img src> / AvatarImage.
 * Raw base64 in DB may omit the data: prefix; JPEG is often mis-tagged as PNG which breaks decoding.
 */
export function normalizeAgentAvatarSrc(
  raw: string | null | undefined,
): string | null {
  if (raw == null || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Handle accidentally quoted values (e.g. "\"https://...\"")
  const unquoted = trimmed.replace(/^['"]+|['"]+$/g, "");
  if (!unquoted) return null;

  if (unquoted.startsWith("data:")) return unquoted;
  if (
    unquoted.startsWith("http://") ||
    unquoted.startsWith("https://") ||
    unquoted.startsWith("blob:")
  ) {
    return preferSameOriginAvatarSrc(unquoted);
  }
  if (unquoted.startsWith("/")) {
    // Keep /images presets same-origin for marketing hosts; absolutize only
    // non-public paths that widgets may need from the app origin.
    if (unquoted.startsWith("/images/")) return unquoted;
    return ensureAbsoluteAppAssetUrl(unquoted);
  }

  // Raw base64 candidate — remove whitespace before magic detection.
  const s = unquoted.replace(/\s/g, "");
  // Raw base64 — infer MIME from magic (base64 prefixes)
  if (s.startsWith("/9j/") || s.startsWith("/9j")) {
    return `data:image/jpeg;base64,${s}`;
  }
  if (s.startsWith("iVBORw0KGgo")) {
    return `data:image/png;base64,${s}`;
  }
  if (s.startsWith("UklGR")) {
    return `data:image/webp;base64,${s}`;
  }
  if (s.startsWith("R0lGOD")) {
    return `data:image/gif;base64,${s}`;
  }

  // Final fallback only if string looks like base64 payload.
  if (/^[A-Za-z0-9+/=]+$/.test(s) && s.length > 80) {
    return `data:image/png;base64,${s}`;
  }
  return null;
}

/** Decode a data-URI / raw-base64 agent photo for HTTP responses (email img src). */
export function parseAgentAvatarImage(
  raw: string | null | undefined,
): { contentType: string; bytes: Buffer } | null {
  const normalized = normalizeAgentAvatarSrc(raw);
  if (!normalized?.startsWith("data:")) return null;
  const match = normalized.match(
    /^data:(image\/[a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/=\s]+)$/,
  );
  if (!match?.[1] || !match[2]) return null;
  try {
    const bytes = Buffer.from(match[2].replace(/\s/g, ""), "base64");
    if (bytes.length < 32) return null;
    return { contentType: match[1], bytes };
  } catch {
    return null;
  }
}

/** Widget / email use: always absolute when the normalized src is an app-relative path. */
export function absoluteAgentAvatarSrc(
  raw: string | null | undefined,
): string | null {
  const normalized = normalizeAgentAvatarSrc(raw);
  if (!normalized) return null;
  if (normalized.startsWith("/") && !normalized.startsWith("//")) {
    return ensureAbsoluteAppAssetUrl(normalized);
  }
  return normalized;
}
